/**
 * Menu path definitions — single source of truth for sidebar navigation AND access control.
 *
 * HOW TO ADD A NEW MENU ITEM
 * ──────────────────────────
 * 1. Add an entry to the relevant section constant below.
 * 2. Set `access` to one of:
 *      "universal"   — any logged-in user can see it
 *      "admin"       — only admins / department heads (isAdmin)
 *      "superuser"   — only is_superuser=true accounts
 *      { entity, action } — requires that CRUD permission, e.g. { entity: "orders", action: "READ" }
 *    Omitting `access` defaults to "universal".
 * 3. That's it — no other file needs to change.
 *
 * DEPARTMENTS PAGE EXAMPLE
 * ──────────────────────────
 * { path: "/dashboard/hr/departments", label: "Departamentos", icon: "Building2",
 *   access: { entity: "departments", action: "READ" } }
 */

import { CALENDAR_MODULES, COMPANY_PROFILE_MODULES, MENU_SECTIONS } from "./menu-sections";
import { isMenuSubGroup } from "./menu-types";

// A API pública continua aqui: quem importa de `@/lib/menu-paths` não muda.
export * from "./menu-types";
export { CALENDAR_MODULES, MENU_SECTIONS } from "./menu-sections";

/**
 * A que secção do menu pertence um caminho — e, por aí, a que módulo.
 *
 * Usado pelo guarda de rotas para fechar páginas de módulos não comprados.
 * Derivar do CAMINHO, e não de uma anotação por rota, é deliberado: uma rota
 * nova fica coberta sem ninguém se lembrar de a marcar. O esquecimento é o
 * modo de falha real — foi assim que `/dashboard/hr/dashboard` continuou
 * acessível a um cliente sem Recursos Humanos.
 *
 * Devolve `undefined` para caminhos que não pertencem a nenhuma secção (o
 * dashboard, as definições, o perfil): esses são sempre acessíveis.
 *
 * A correspondência é por prefixo, para apanhar sub-rotas e parâmetros —
 * `/dashboard/hr/employees/123` resolve para a secção de `/dashboard/hr/...`.
 */
export function sectionOfPath(pathname: string): string | undefined {
  let melhor: { id: string; tamanho: number } | undefined;

  for (const section of MENU_SECTIONS) {
    for (const item of section.items) {
      const itens = isMenuSubGroup(item) ? item.items : [item];
      for (const it of itens) {
        if (pathname === it.path || pathname.startsWith(`${it.path}/`)) {
          // O prefixo mais longo ganha: `/dashboard/hr/trainings` é mais
          // específico do que `/dashboard/hr`, e pode ser de outra secção.
          //
          // Um item com `module` próprio (ex.: Amostras Técnicas, visível em
          // "Comercial" mas exigindo só `comercial_avancado` — ADR-0006) devolve
          // ESSE módulo, não o id da secção — senão `moduleOfPath` mandava
          // `ProtectedRoute` verificar o módulo errado e uma instalação com
          // `comercial_avancado` sem `comercial` via a rota bloqueada por um
          // módulo que nunca precisou de ter.
          if (!melhor || it.path.length > melhor.tamanho) {
            melhor = { id: it.module ?? section.id, tamanho: it.path.length };
          }
        }
      }
    }
  }
  return melhor?.id;
}

/** Prefixo de URL → secção de menu, para rotas que NÃO estão no menu.
 *
 * O menu não conhece todas as rotas: detalhes (`/production/:id`), redirects,
 * ecrãs de configuração (`/hr/primavera-rules`). Sem este mapa essas rotas
 * caíam em `/dashboard` — que todo o cliente tem — e ficavam ABERTAS a quem
 * não comprou o módulo. Foram 20 rotas nessa situação.
 *
 * A ordem importa: o primeiro prefixo que bate ganha, e os mais específicos
 * vêm primeiro. */
const PREFIXO_PARA_SECCAO: ReadonlyArray<readonly [string, string]> = [
  ["/dashboard/hr/", "recursos-humanos"],
  ["/dashboard/energia/", "energia"],
  ["/dashboard/reception/", "rececao"],
  ["/dashboard/security", "seguranca"],
  // Mapa de produção e artigos mudaram para `artigos` a 2026-09-01 — a
  // ficha técnica é independente de quem fabrica. A listagem "Produções"
  // (`/dashboard/productions`) foi absorvida por `/dashboard/factory-orders`
  // a 2026-09-04 — era a mesma tabela filtrada por estado.
  ["/dashboard/production", "ordens-fabrico"],
  ["/dashboard/factory-orders", "ordens-fabrico"],
  ["/dashboard/articles", "catalogo-artigos"],
  // Regras de pontuação da avaliação de fornecedores — não está no menu (é
  // configuração), mas é a mesma entidade `supplier_evaluations` de
  // Produção. Tem de vir ANTES do prefixo genérico de `/dashboard/suppliers`.
  ["/dashboard/suppliers/scoring-rules", "producao"],
  // A ficha de Fornecedor (lista, detalhe) é `terceiros`; as sub-rotas de
  // avaliação continuam `producao` — estão explícitas no menu e resolvem por
  // prefixo mais específico antes de cair aqui.
  ["/dashboard/suppliers", "clientes-fornecedores"],
  // A ficha de Cliente, com todas as suas abas aninhadas (encomendas,
  // artigos, produção, expedição DESTE cliente), é `terceiros` desde
  // 2026-09-01 — antes era tratada como bloco único de `comercial`.
  ["/dashboard/clients", "clientes-fornecedores"],
  // Contactos — LEVANTAMENTO-GAP.md 3.2, mesma entidade/domínio `terceiros`.
  ["/dashboard/contacts", "clientes-fornecedores"],
  ["/dashboard/orders", "comercial"],
  ["/dashboard/leads", "comercial"],
  ["/dashboard/expeditions", "comercial"],
];

/**
 * "Qualquer um destes módulos" que a rota exige, se a houver.
 *
 * `moduleOfPath` devolve UM módulo; o Calendário precisa de um de vários. O
 * endereço antigo da Vista Geral (redirect para o novo) partilha a regra —
 * senão ficava preso ao módulo de RH, e um perfil só com Receção perdia o link.
 * Devolve `undefined` para todas as outras rotas: aí vale `moduleOfPath`.
 */
const ANY_MODULE_ALIASES: Record<string, string[]> = {
  "/dashboard/hr/calendar/overview": CALENDAR_MODULES,
  "/dashboard/hr/calendar/individual": CALENDAR_MODULES,
  "/dashboard/hr/calendar/team": CALENDAR_MODULES,
  "/dashboard/hr/calendar/company": CALENDAR_MODULES,
  "/dashboard/hr/calendar/meeting-rooms": CALENDAR_MODULES,
  // As salas alimentam o Calendário: existem onde ele existe. A rota vive nas
  // Definições (núcleo), por isso o gate de módulo tem de ser explícito.
  "/dashboard/settings/salas-de-reuniao": CALENDAR_MODULES,
  // O perfil da empresa só alimenta documentos de módulos que o cliente pode não ter (ver `COMPANY_PROFILE_MODULES`).
  "/dashboard/settings/empresa": COMPANY_PROFILE_MODULES,
};

export function anyModuleOfPath(pathname: string): string[] | undefined {
  let melhor: { modules: string[]; tamanho: number } | undefined;
  const candidatos: Array<[string, string[]]> = [...Object.entries(ANY_MODULE_ALIASES)];
  for (const section of MENU_SECTIONS) {
    for (const item of section.items) {
      for (const it of isMenuSubGroup(item) ? item.items : [item]) {
        if (it.anyModule) candidatos.push([it.path, it.anyModule]);
      }
    }
  }
  for (const [path, modules] of candidatos) {
    if ((pathname === path || pathname.startsWith(`${path}/`)) && (!melhor || path.length > melhor.tamanho)) {
      melhor = { modules, tamanho: path.length };
    }
  }
  return melhor?.modules;
}

/** A secção de menu que cobre este caminho — para cruzar com o perfil.
 *
 * Primeiro procura no menu (o prefixo mais específico ganha); se nada bater,
 * cai no mapa de prefixos acima. Uma rota que não pertença a nenhum dos dois
 * é do núcleo e fica sempre acessível. */
export function moduleOfPath(pathname: string): string | undefined {
  // O mapa de prefixos é consultado SEMPRE, e não só quando o menu falha.
  //
  // Devolver o resultado do menu à primeira deixava uma rota fora do menu
  // presa à secção de um item de menu que fosse seu prefixo. Foi o que
  // aconteceu ao partir `producao` a 2026-09-01: `/dashboard/suppliers`
  // passou a `terceiros`, e `/dashboard/suppliers/scoring-rules` — que é
  // `supplier_evaluations`, logo `producao` — passou a resolver para
  // `terceiros` também. Um cliente com a ficha de fornecedor mas sem o módulo
  // de Produção abria a página de regras de pontuação, que só falhava depois,
  // nas chamadas à API. O gate existe precisamente para fechar antes de
  // renderizar.
  //
  // Vence o prefixo mais longo, venha de onde vier — é a mesma regra que
  // `sectionOfPath` já aplica dentro do menu.
  const doMenu = sectionOfPath(pathname);
  const tamanhoDoMenu = doMenu ? _comprimentoNoMenu(pathname) : 0;

  let doMapa: { seccao: string; tamanho: number } | undefined;
  for (const [prefixo, seccao] of PREFIXO_PARA_SECCAO) {
    if (pathname === prefixo || pathname.startsWith(prefixo)) {
      doMapa = { seccao, tamanho: prefixo.length };
      break; // a lista está ordenada do mais específico para o mais genérico
    }
  }

  // `dashboard` é a secção do painel, que todo o cliente tem. Uma rota de
  // módulo que lá resolvesse ficava aberta, por isso o mapa ganha-lhe sempre.
  if (doMenu && doMenu !== "dashboard" && tamanhoDoMenu >= (doMapa?.tamanho ?? 0)) {
    return doMenu;
  }
  return doMapa?.seccao ?? doMenu;
}

/** O comprimento do prefixo de menu que cobre este caminho.
 *
 * `sectionOfPath` já o calcula para escolher o vencedor, mas só devolve o id
 * da secção. Aqui é preciso o comprimento para comparar com o mapa de
 * prefixos — sem ele não há como saber qual dos dois é mais específico. */
function _comprimentoNoMenu(pathname: string): number {
  let maior = 0;
  for (const section of MENU_SECTIONS) {
    for (const item of section.items) {
      const caminhos = isMenuSubGroup(item) ? item.items.map((i) => i.path) : [item.path];
      for (const caminho of caminhos) {
        if ((pathname === caminho || pathname.startsWith(`${caminho}/`)) && caminho.length > maior) {
          maior = caminho.length;
        }
      }
    }
  }
  return maior;
}
